import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface Notification {
  id: string
  tip: 'success' | 'error' | 'warning' | 'info'
  mesaj: string
  baslik?: string
  sure?: number // ms, 0 = kalıcı
}

export type RehberSutun = { id: string; label: string; aktif: boolean; sira: number }

export const STOK_DEFAULT_SUTUNLAR: RehberSutun[] = [
  { id: 'kod',        label: 'Kodu',        aktif: true,  sira: 0  },
  { id: 'ad',         label: 'Stok Adı',    aktif: true,  sira: 1  },
  { id: 'grupKodu',   label: 'Grup Kodu',   aktif: true,  sira: 2  },
  { id: 'barkod',     label: 'Barkod',      aktif: false, sira: 3  },
  { id: 'alisFiyati', label: 'Alış Fiyatı', aktif: false, sira: 4  },
  { id: 'fiyat1',     label: 'Fiyatı 1',    aktif: true,  sira: 5  },
  { id: 'fiyat2',     label: 'Fiyatı 2',    aktif: false, sira: 6  },
  { id: 'fiyat3',     label: 'Fiyatı 3',    aktif: false, sira: 7  },
  { id: 'miktar',     label: 'Miktar',      aktif: true,  sira: 8  },
  { id: 'birim',      label: 'Birim',       aktif: false, sira: 9  },
  { id: 'kdv',        label: 'KDV',         aktif: true,  sira: 10 },
  { id: 'ozelKod1',   label: 'Kod 1',       aktif: false, sira: 11 },
  { id: 'ozelKod2',   label: 'Kod 2',       aktif: false, sira: 12 },
  { id: 'gtip',       label: 'GTIP',        aktif: false, sira: 13 },
  { id: 'resim',      label: 'Resim',       aktif: false, sira: 14 },
]

export const CARI_DEFAULT_SUTUNLAR: RehberSutun[] = [
  { id: 'kod',          label: 'Kod',            aktif: true,  sira: 0  },
  { id: 'unvan',        label: 'Cari Adı/Ünvan', aktif: true,  sira: 1  },
  { id: 'telefon',      label: 'Telefon',         aktif: true,  sira: 2  },
  { id: 'bakiye',       label: 'Bakiye',          aktif: true,  sira: 3  },
  { id: 'tip',          label: 'Cari Tipi',       aktif: false, sira: 4  },
  { id: 'vkntc',        label: 'VKN/TC',          aktif: false, sira: 5  },
  { id: 'vergiDairesi', label: 'Vergi Dairesi',   aktif: false, sira: 6  },
  { id: 'grupKodu',     label: 'Grup Kodu',       aktif: false, sira: 7  },
  { id: 'ozelKod1',     label: 'Kod 1',           aktif: false, sira: 8  },
  { id: 'ozelKod2',     label: 'Kod 2',           aktif: false, sira: 9  },
  { id: 'yetkili',      label: 'Yetkili',         aktif: false, sira: 10 },
  { id: 'telefon2',     label: 'Telefon 2',       aktif: false, sira: 11 },
  { id: 'il',           label: 'İl',              aktif: false, sira: 12 },
  { id: 'ilce',         label: 'İlçe',            aktif: false, sira: 13 },
]

interface UIState {
  sidebarCollapsed: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void

  mobileSidebarOpen: boolean
  toggleMobileSidebar: () => void
  closeMobileSidebar: () => void

  tema: 'dark' | 'light'
  toggleTema: () => void

  palet: 'indigo' | 'emerald' | 'rose' | 'amber' | 'slate' | 'modern' | 'klasik' | 'ferah'
  setPalet: (palet: 'indigo' | 'emerald' | 'rose' | 'amber' | 'slate' | 'modern' | 'klasik' | 'ferah') => void

  calismaYili: number
  setCalismaYili: (yil: number) => void

  stokRehberDepoAyri: boolean
  setStokRehberDepoAyri: (val: boolean) => void

  stokRehberSutunlar: RehberSutun[]
  setStokRehberSutunlar: (cols: RehberSutun[]) => void

  cariRehberSutunlar: RehberSutun[]
  setCariRehberSutunlar: (cols: RehberSutun[]) => void

  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id'>) => void
  removeNotification: (id: string) => void
  notify: (mesaj: string, tip?: Notification['tip'], baslik?: string) => void

  sonRaporlar: { id: string; ad: string }[]
  raporKaydet: (rapor: { id: string; ad: string }) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      mobileSidebarOpen: false,
      toggleMobileSidebar: () => set((s) => ({ mobileSidebarOpen: !s.mobileSidebarOpen })),
      closeMobileSidebar: () => set({ mobileSidebarOpen: false }),

      tema: 'light',
      toggleTema: () => {
        const newTema = get().tema === 'dark' ? 'light' : 'dark'
        document.documentElement.classList.toggle('dark', newTema === 'dark')
        set({ tema: newTema })
      },

      palet: 'modern',
      setPalet: (palet) => {
        document.documentElement.dataset.theme = palet
        set({ palet })
      },

      calismaYili: new Date().getFullYear(),
      setCalismaYili: (yil) => set({ calismaYili: yil }),

      stokRehberDepoAyri: false,
      setStokRehberDepoAyri: (val) => set({ stokRehberDepoAyri: val }),

      stokRehberSutunlar: STOK_DEFAULT_SUTUNLAR,
      setStokRehberSutunlar: (cols) => set({ stokRehberSutunlar: cols }),

      cariRehberSutunlar: CARI_DEFAULT_SUTUNLAR,
      setCariRehberSutunlar: (cols) => set({ cariRehberSutunlar: cols }),

      notifications: [],
      addNotification: (notification) => {
        const id = `notif-${Date.now()}`
        set((s) => ({ notifications: [...s.notifications, { ...notification, id }] }))
        const sure = notification.sure ?? 4000
        if (sure > 0) {
          setTimeout(() => get().removeNotification(id), sure)
        }
      },
      removeNotification: (id) =>
        set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),

      notify: (mesaj, tip = 'info', baslik) => {
        get().addNotification({ mesaj, tip, baslik })
      },

      sonRaporlar: [],
      raporKaydet: (rapor) => {
        set((s) => {
          const filtered = s.sonRaporlar.filter((r) => r.id !== rapor.id)
          return { sonRaporlar: [rapor, ...filtered].slice(0, 5) }
        })
      },
    }),
    {
      name: 'nova-ui',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        tema: state.tema,
        palet: state.palet,
        calismaYili: state.calismaYili,
        stokRehberDepoAyri: state.stokRehberDepoAyri,
        stokRehberSutunlar: state.stokRehberSutunlar,
        cariRehberSutunlar: state.cariRehberSutunlar,
        sonRaporlar: state.sonRaporlar,
      }),
    }
  )
)
